import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { FILTER_NAMES } from '../../components/filters/hooks/filter-names.constants';
import {
  getFavouriteListService,
  getFavouriteValueService,
  removeFavouriteValueService,
  setFavouriteValueService,
} from '../../services/get-favourite.service';
import { FavouriteValue } from './interfaces/favourite-value.interface';
import { FilterSelectedValue } from './interfaces/filter-selected-value.interface';
const FAVOURITE_QUERY_KEY = 'set-favourite/REACT_QUERY_KEY';
const FAVOURITE_LIST_QUERY_KEY = 'list-favourite/REACT_QUERY_KEY';

export interface AppFavouriteValue {
  id: string;
  selectedValue: FavouriteValue | undefined | null;
  setSelectedValue: (selectedValue: FavouriteValue) => Promise<FavouriteValue>;
  removeSelectedValue: () => Promise<void>;
  isFetching: boolean;
}

export const useAppFavouriteValue = (
  id: string,
  listId: string,
): AppFavouriteValue => {
  const queryClient = useQueryClient();
  const { data: selectedValue, isFetching } = useQuery({
    queryKey: [FAVOURITE_QUERY_KEY, id],
    queryFn: () => getFavouriteValueService(id),
    placeholderData: keepPreviousData,
  });
  const setSelectedValue = useCallback(
    async (selVal: FavouriteValue) => {
      setFavouriteValueService(selVal, listId);
      queryClient.refetchQueries({ queryKey: [FAVOURITE_QUERY_KEY, id] });
      queryClient.refetchQueries({ queryKey: [FAVOURITE_LIST_QUERY_KEY] });

      return selVal;
    },
    [queryClient, id, listId],
  );
  const removeSelectedValue = useCallback(async () => {
    removeFavouriteValueService(id, listId);
    queryClient.refetchQueries({ queryKey: [FAVOURITE_QUERY_KEY, id] });
    queryClient.refetchQueries({ queryKey: [FAVOURITE_LIST_QUERY_KEY] });
    return;
  }, [queryClient, id, listId]);

  const value = useMemo(
    (): AppFavouriteValue => ({
      id,
      isFetching,
      selectedValue,
      setSelectedValue,
      removeSelectedValue,
    }),
    [setSelectedValue, removeSelectedValue, selectedValue, isFetching, id],
  );
  return value;
};

export const useAppFavouriteId = (
  filterSelection: FilterSelectedValue[],
  path: string,
  prefix: string,
  defaultValue: string = prefix,
) => {
  const result = useMemo(() => {
    const marketMultiValue = filterSelection.find(
      (i) => i.id === FILTER_NAMES.MARKETMULTI,
    );
    const marketSingleValue = filterSelection.find(
      (i) => i.id === FILTER_NAMES.MARKET,
    );
    const marketValue = marketMultiValue ?? marketSingleValue;
    const market = marketValue?.values?.map((i) => i.value).join(',');
    const metaCategory = filterSelection.find(
      (i) => i.id === FILTER_NAMES.METACATEGORY,
    )?.values[0]?.value;
    if (market !== undefined && metaCategory !== undefined) {
      return `${prefix}${path}-${market}-${metaCategory}`;
    } else {
      return defaultValue;
    }
  }, [filterSelection, path, prefix, defaultValue]);
  return result;
};
export const useAppFavouriteDefaultTitle = (
  filterSelection: FilterSelectedValue[],
) => {
  const result = useMemo(() => {
    const marketSingleValue = filterSelection.find(
      (i) => i.id === FILTER_NAMES.MARKET,
    );
    const marketMultiValue = filterSelection.find(
      (i) => i.id === FILTER_NAMES.MARKETMULTI,
    );

    const market = marketMultiValue
      ? 'Multiple Selected'
      : marketSingleValue?.values[0]?.label;
    const metaCategory = filterSelection.find(
      (i) => i.id === FILTER_NAMES.METACATEGORY,
    )?.values[0]?.label;
    return `${metaCategory} - ${market}`;
  }, [filterSelection]);
  return result;
};

export interface AppFavouriteList {
  list: FavouriteValue[] | undefined;
  isFetching: boolean;
}

export const useAppFavouriteList = (listId: string) => {
  const { data: list = [], isFetching } = useQuery({
    queryKey: [FAVOURITE_LIST_QUERY_KEY, listId],
    queryFn: () => getFavouriteListService(listId),
    placeholderData: keepPreviousData,
  });

  const value = useMemo(
    (): AppFavouriteList => ({
      isFetching,
      list,
    }),
    [list, isFetching],
  );
  return value;
};
